import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { stateGuides } from "@/data/stateGuides";
import { Loader2, Sparkles, Play } from "lucide-react";

interface StateRow {
  id: string;
  slug: string;
  name: string;
  topic_count: number;
  has_guide: boolean;
}

interface PreviewTopic {
  title: string;
  content: string;
  replies: { content: string; voice: string }[];
}

const AdminSeedForums = () => {
  const { toast } = useToast();
  const [seeding, setSeeding] = useState<string | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [dryRun, setDryRun] = useState(false);
  const [force, setForce] = useState(false);
  const [preview, setPreview] = useState<{ state: string; topics: PreviewTopic[] } | null>(null);

  const { data: rows, isLoading, refetch } = useQuery<StateRow[]>({
    queryKey: ["admin-seed-states"],
    queryFn: async () => {
      const { data: parents } = await supabase
        .from("categories")
        .select("id")
        .in("slug", ["youth-soccer-forum-usa", "youth-soccer-forum-canada"]);
      const parentIds = (parents ?? []).map((p) => p.id);

      const { data: cats, error } = await supabase
        .from("categories")
        .select("id, slug, name")
        .eq("level", 2)
        .in("parent_category_id", parentIds)
        .order("slug");
      if (error) throw error;

      const ids = (cats ?? []).map((c) => c.id);
      const { data: topicRows } = await supabase
        .from("topics")
        .select("category_id")
        .in("category_id", ids);

      const counts = new Map<string, number>();
      (topicRows ?? []).forEach((t) => {
        counts.set(t.category_id!, (counts.get(t.category_id!) ?? 0) + 1);
      });

      return (cats ?? []).map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
        topic_count: counts.get(c.id) ?? 0,
        has_guide: !!stateGuides[c.slug],
      }));
    },
  });

  const seedable = useMemo(
    () => (rows ?? []).filter((r) => r.has_guide && (force || r.topic_count < 5)),
    [rows, force],
  );

  const seedOne = async (
    row: StateRow,
  ): Promise<{ ok: boolean; message: string; topics?: PreviewTopic[] }> => {
    const guide = stateGuides[row.slug];
    if (!guide) return { ok: false, message: "No guide data" };

    const { data, error } = await supabase.functions.invoke("seed-state-forum-topics", {
      body: { stateSlug: row.slug, guide, dryRun, force },
    });
    if (error) return { ok: false, message: error.message };
    if (data?.error) return { ok: false, message: data.error };
    if (data?.skipped) return { ok: false, message: data.reason };
    if (data?.dryRun)
      return {
        ok: true,
        message: `Dry run: ${data.generated} topics generated`,
        topics: data.topics ?? [],
      };
    return {
      ok: true,
      message: `Inserted ${data.insertedTopics} topics, ${data.insertedPosts} replies`,
    };
  };

  const handleSeed = async (row: StateRow) => {
    setSeeding(row.slug);
    setPreview(null);
    const res = await seedOne(row);
    if (res.ok && res.topics) {
      setPreview({ state: row.name, topics: res.topics });
    }
    toast({
      title: res.ok ? `${row.name}: ${res.message}` : `${row.name} failed`,
      description: res.ok ? undefined : res.message,
      variant: res.ok ? "default" : "destructive",
    });
    setSeeding(null);
    refetch();
  };

  const handleBulk = async () => {
    if (!seedable.length) return;
    if (!confirm(`Seed ${seedable.length} states? This will call the AI for each.`)) return;
    setBulkRunning(true);
    setProgress({ done: 0, total: seedable.length });
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < seedable.length; i++) {
      const row = seedable[i];
      setSeeding(row.slug);
      const res = await seedOne(row);
      if (res.ok) okCount++;
      else failCount++;
      setProgress({ done: i + 1, total: seedable.length });
    }
    setSeeding(null);
    setBulkRunning(false);
    toast({
      title: "Bulk seed complete",
      description: `${okCount} succeeded, ${failCount} failed/skipped`,
    });
    refetch();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Seed State Forums</h1>
        <p className="text-muted-foreground">
          Generate evergreen, state-specific seed discussions for empty forums using
          Lovable AI grounded in the curated state guides.
        </p>
      </div>

      <Card className="p-4 flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-2">
          <Switch id="dry" checked={dryRun} onCheckedChange={setDryRun} />
          <Label htmlFor="dry">Dry run (preview only, no inserts)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Switch id="force" checked={force} onCheckedChange={setForce} />
          <Label htmlFor="force">Force (allow even if ≥5 topics)</Label>
        </div>
        <div className="ml-auto flex items-center gap-3">
          {progress && (
            <span className="text-sm text-muted-foreground">
              {progress.done}/{progress.total}
            </span>
          )}
          <Button
            onClick={handleBulk}
            disabled={bulkRunning || !seedable.length}
            className="gap-2"
          >
            {bulkRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            Seed all empty ({seedable.length})
          </Button>
        </div>
      </Card>

      {preview && (
        <Card className="p-6 space-y-4 border-primary/40">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold">
                Dry run preview — {preview.state}
              </h2>
              <p className="text-sm text-muted-foreground">
                {preview.topics.length} topics generated. Nothing was saved.
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}>
              Clear
            </Button>
          </div>
          <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
            {preview.topics.map((t, idx) => (
              <div key={idx} className="border rounded-md p-4 space-y-2 bg-muted/30">
                <div className="font-semibold">
                  {idx + 1}. {t.title}
                </div>
                <p className="text-sm whitespace-pre-wrap text-muted-foreground">
                  {t.content}
                </p>
                <div className="pl-4 border-l-2 border-primary/30 space-y-2 mt-2">
                  {t.replies.map((r, ri) => (
                    <div key={ri} className="text-sm">
                      <Badge variant="outline" className="mr-2 text-xs">
                        {r.voice}
                      </Badge>
                      <span className="whitespace-pre-wrap">{r.content}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8">Loading states…</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>State / Province</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Topics</TableHead>
                  <TableHead>Guide</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(rows ?? []).map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{r.slug}</TableCell>
                    <TableCell>
                      <Badge variant={r.topic_count === 0 ? "destructive" : "secondary"}>
                        {r.topic_count}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {r.has_guide ? (
                        <Badge variant="outline">Yes</Badge>
                      ) : (
                        <Badge variant="destructive">Missing</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          !r.has_guide ||
                          seeding === r.slug ||
                          bulkRunning ||
                          (!force && r.topic_count >= 5)
                        }
                        onClick={() => handleSeed(r)}
                        className="gap-2"
                      >
                        {seeding === r.slug ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        Seed
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>
    </div>
  );
};

export default AdminSeedForums;
